import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Plus, X, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray100: '#F3F4F6', gray500: '#6B7280',
  gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info: '#1E40AF', infoBg: '#DBEAFE',
  green: '#166534', greenBg: '#DCFCE7',
};

const BN = {
  title: 'Slip Perubatan / Pelepasan', subtitle: 'rekod', back: '← Laman Utama',
  addSlip: '+ Tambah Slip', allType: 'Semua Jenis', allStatus: 'Semua Status',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Rekod Slip', emptyBody: 'Tiada slip perubatan/pelepasan direkodkan.',
  addTitle: 'Rekod Slip Perubatan/Pelepasan',
  studentId: 'ID Pelajar *', slipType: 'Jenis Slip *',
  absenceDate: 'Tarikh Ketidakhadiran *', returnDate: 'Tarikh Kembali',
  issuedBy: 'Dikeluarkan Oleh *', clinicHospital: 'Klinik/Hospital',
  reason: 'Sebab Ketidakhadiran', documentUrl: 'URL Dokumen',
  save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  approve: 'Luluskan', reject: 'Tolak',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
  totalSlips: 'Jumlah Slip', approved: 'Diluluskan', pending: 'Menunggu',
};
const EN = {
  title: 'Medical / Excuse Slips', subtitle: 'records', back: '← Home',
  addSlip: '+ Add Slip', allType: 'All Types', allStatus: 'All Status',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No Excuse Slips', emptyBody: 'No medical/excuse slips recorded.',
  addTitle: 'Record Medical/Excuse Slip',
  studentId: 'Student ID *', slipType: 'Slip Type *',
  absenceDate: 'Absence Date *', returnDate: 'Return Date',
  issuedBy: 'Issued By *', clinicHospital: 'Clinic/Hospital',
  reason: 'Reason for Absence', documentUrl: 'Document URL',
  save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  approve: 'Approve', reject: 'Reject',
  retry: 'Retry', errLoad: 'Failed to load.',
  totalSlips: 'Total Slips', approved: 'Approved', pending: 'Pending',
};

const SLIP_TYPES = ['MEDICAL','EXCUSE','EMERGENCY_LEAVE','SPORTS_COMPETITION','OFFICIAL_DUTY'];
const TYPE_CFG: Record<string, { ms: string; en: string; color: string; bg: string }> = {
  MEDICAL:            { ms: 'Perubatan',         en: 'Medical',             color: '#166534', bg: '#DCFCE7' },
  EXCUSE:             { ms: 'Pelepasan Am',       en: 'Excuse',              color: '#1E40AF', bg: '#DBEAFE' },
  EMERGENCY_LEAVE:    { ms: 'Cuti Kecemasan',    en: 'Emergency Leave',     color: '#991B1B', bg: '#FEE2E2' },
  SPORTS_COMPETITION: { ms: 'Pertandingan Sukan', en: 'Sports Competition',  color: '#0F766E', bg: '#CCFBF1' },
  OFFICIAL_DUTY:      { ms: 'Tugas Rasmi',        en: 'Official Duty',       color: '#B45309', bg: '#FEF9C3' },
};
const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  PENDING:  { label: 'Menunggu',  color: '#92400E', bg: '#FEF3C7' },
  APPROVED: { label: 'Diluluskan', color: '#166534', bg: '#DCFCE7' },
  REJECTED: { label: 'Ditolak',   color: '#991B1B', bg: '#FEE2E2' },
};

interface Props { user: AuthUser }
interface ExcuseSlip {
  id: string; student_id: string; slip_type: string; absence_date: string;
  return_date: string | null; issued_by: string; clinic_hospital: string | null;
  reason: string | null; document_url: string | null; status: string;
  full_name_en: string | null; full_name_ms: string | null; ic_number: string; class_name: string | null;
}
interface PageResult { data: ExcuseSlip[]; total: number; page: number }
const LIMIT = 20;
const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';

export function ExcuseSlipsPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit   = hasRole(user, 'super_admin', 'admin', 'principal', 'teacher', 'clerk');
  const canVerify = hasRole(user, 'super_admin', 'admin', 'principal');
  const schoolId  = schoolIdFromUser(user) ?? '';

  const [slips,   setSlips]   = useState<ExcuseSlip[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [form, setForm] = useState({
    studentId: '', slipType: 'MEDICAL', absenceDate: '', returnDate: '',
    issuedBy: '', clinicHospital: '', reason: '', documentUrl: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ schoolId, page: String(page) });
      if (typeFilter)   p.set('slipType', typeFilter);
      if (statusFilter) p.set('status', statusFilter);
      const res = await apiGet<PageResult>(`/api/excuse-slips?${p}`);
      setSlips(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, typeFilter, statusFilter, UI.errLoad]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.studentId.trim() || !form.absenceDate || !form.issuedBy.trim()) return;
    setSaving(true);
    try {
      await apiPost('/api/excuse-slips', {
        studentId: form.studentId, slipType: form.slipType, absenceDate: form.absenceDate,
        returnDate: form.returnDate || undefined, issuedBy: form.issuedBy,
        clinicHospital: form.clinicHospital || undefined, reason: form.reason || undefined,
        documentUrl: form.documentUrl || undefined, schoolId,
      });
      setShowAdd(false);
      setForm({ studentId: '', slipType: 'MEDICAL', absenceDate: '', returnDate: '', issuedBy: '', clinicHospital: '', reason: '', documentUrl: '' });
      setToast({ message: lang === 'en' ? 'Slip recorded.' : 'Slip direkodkan.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiPatch(`/api/excuse-slips/${id}/status`, { status });
      setToast({ message: lang === 'en' ? 'Status updated.' : 'Status dikemas kini.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900, backgroundColor: C.white, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileCheck size={20} color={C.green} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && <button onClick={() => setShowAdd(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>{UI.addSlip}</button>}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: UI.totalSlips, value: total, color: C.green, bg: C.greenBg },
          { label: UI.approved, value: slips.filter(s => s.status === 'APPROVED').length, color: C.success, bg: C.successBg },
          { label: UI.pending, value: slips.filter(s => s.status === 'PENDING').length, color: C.warning, bg: C.warningBg },
        ].map(card => (
          <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: card.color }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { val: typeFilter, set: setTypeFilter, opts: SLIP_TYPES.map(t => ({ v: t, l: lang === 'en' ? TYPE_CFG[t]?.en ?? t : TYPE_CFG[t]?.ms ?? t })), placeholder: UI.allType },
          { val: statusFilter, set: setStatusFilter, opts: Object.keys(STATUS_CFG).map(s => ({ v: s, l: STATUS_CFG[s]!.label })), placeholder: UI.allStatus },
        ].map(({ val, set, opts, placeholder }) => (
          <select key={placeholder} value={val} onChange={e => { set(e.target.value); setPage(1); }} style={{ ...inputStyle, width: '180px' }}>
            <option value="">{placeholder}</option>
            {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
      {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}

      {!loading && slips.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
          <FileCheck size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
          <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
        </div>
      )}

      <div style={{ display: 'grid', gap: '10px' }}>
        {slips.map(s => {
          const tc = TYPE_CFG[s.slip_type];
          const sc = STATUS_CFG[s.status];
          return (
            <div key={s.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: C.gray900 }}>
                    {lang === 'en' ? (s.full_name_en ?? s.ic_number) : (s.full_name_ms ?? s.full_name_en ?? s.ic_number)}
                    {s.class_name ? <span style={{ fontWeight: 400, color: C.gray500, fontSize: '12px' }}> · {s.class_name}</span> : null}
                  </div>
                  <div style={{ fontSize: '13px', color: C.gray700, marginTop: '2px' }}>
                    {new Date(s.absence_date).toLocaleDateString('ms-BN')}
                    {s.return_date ? ` → ${new Date(s.return_date).toLocaleDateString('ms-BN')}` : ''}
                  </div>
                  <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px' }}>
                    {UI.issuedBy.replace(' *','')}: {s.issued_by}
                    {s.clinic_hospital ? ` · ${s.clinic_hospital}` : ''}
                  </div>
                  {s.reason && <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px', fontStyle: 'italic' }}>{s.reason}</div>}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0, marginLeft: '12px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: tc?.bg, color: tc?.color }}>{lang === 'en' ? tc?.en : tc?.ms}</span>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: sc?.bg, color: sc?.color }}>{sc?.label}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center' }}>
                {s.document_url && (
                  <a href={s.document_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: C.info, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Upload size={12} />{lang === 'en' ? 'View Document' : 'Lihat Dokumen'}
                  </a>
                )}
                {canVerify && s.status === 'PENDING' && (
                  <>
                    <button onClick={() => updateStatus(s.id, 'APPROVED')} style={{ padding: '4px 12px', borderRadius: '6px', background: C.successBg, border: 'none', color: C.success, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>{UI.approve}</button>
                    <button onClick={() => updateStatus(s.id, 'REJECTED')} style={{ padding: '4px 12px', borderRadius: '6px', background: C.errorBg, border: 'none', color: C.error, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>{UI.reject}</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
          <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
            <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '500px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addTitle}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { key: 'studentId', label: UI.studentId, type: 'text' },
                { key: 'absenceDate', label: UI.absenceDate, type: 'date' },
                { key: 'returnDate', label: UI.returnDate, type: 'date' },
                { key: 'issuedBy', label: UI.issuedBy, type: 'text' },
                { key: 'clinicHospital', label: UI.clinicHospital, type: 'text' },
                { key: 'documentUrl', label: UI.documentUrl, type: 'url' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.slipType}</label>
                <select value={form.slipType} onChange={e => setForm(p => ({ ...p, slipType: e.target.value }))} style={inputStyle}>
                  {SLIP_TYPES.map(t => <option key={t} value={t}>{lang === 'en' ? TYPE_CFG[t]?.en : TYPE_CFG[t]?.ms}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{UI.reason}</label>
                <textarea value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))}
                  style={{ ...inputStyle, height: '60px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
